import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Input } from "./ui/input";
import { HindiInput } from "./HindiInput";
import { TitlePicker } from "./TitlePicker";
import { GROUPS, personTitle, splitTitle } from "../data/rssbData";
import { hinglishToHindi } from "../lib/translit";
import { toast } from "sonner";

const selCls = "w-full mt-1 bg-[#f6eeda] border border-[#e3d6b6] rounded-md px-3 py-2 text-sm text-[#3a1212] font-dev";
const inCls = "mt-1 bg-[#f6eeda] border-[#e3d6b6] text-[#3a1212] font-dev";

export const PersonDialog = ({ open, onClose, kind, person, onSave }) => {
  const [f, setF] = useState({});
  const [saving, setSaving] = useState(false);
  const isPreacher = kind === "preacher";

  useEffect(() => {
    if (open) {
      setF({
        title: person ? personTitle(person) || "श्री" : "श्री",
        name: person?.hi || "",
        en: person?.en || "",
        role: person?.role || "SR",
        place: person?.placeHi || "",
        group: String(person?.group || 1),
        mobile: person?.mobile || "",
      });
    }
  }, [open, person]);

  const upd = (k, v) => setF((s) => ({ ...s, [k]: v, ...(k === "name" && /[A-Za-z]/.test(v) ? { en: v } : {}) }));

  const save = async () => {
    if (!f.name?.trim()) {
      toast.error("नाम आवश्यक है");
      return;
    }
    setSaving(true);
    const hi = splitTitle(await hinglishToHindi(f.name.trim())).rest;
    const placeHi = (await hinglishToHindi(f.place.trim())) || "";
    const next = {
      ...(person || { id: (isPreacher ? "s" : "p") + Date.now().toString(36) }),
      hi,
      en: f.en?.trim() || hi,
      title: f.title,
      placeHi,
      group: Number(f.group),
      mobile: f.mobile.trim(),
      ...(isPreacher ? { role: f.role } : {}),
    };
    setSaving(false);
    onSave(next);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#fffdf7] border-[#6e1b1b]/40 text-[#3a1212] max-h-[90vh] overflow-y-auto" data-testid="person-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif-dev text-[#3a1212]">
            {person ? "विवरण बदलें" : "नया जोड़ें"} — {isPreacher ? "SR / SK" : "पाठी"}
          </DialogTitle>
          <DialogDescription className="text-xs text-[#7a4a3a] font-dev">
            नाम हिंग्लिश में लिखें और नीचे से सही हिंदी वर्तनी चुनें
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-1">
          <div>
            <Label className="text-xs text-[#5a2a24]">संबोधन</Label>
            <div className="mt-1"><TitlePicker testid="person-title" value={f.title} onChange={(v) => upd("title", v)} /></div>
          </div>
          <div>
            <Label className="text-xs text-[#5a2a24]">नाम</Label>
            <HindiInput testid="person-name" value={f.name || ""} onChange={(v) => upd("name", v)} placeholder="e.g. Ram Kumar" className={inCls} />
          </div>
          {isPreacher && (
            <div>
              <Label className="text-xs text-[#5a2a24]">भूमिका</Label>
              <select data-testid="person-role" value={f.role} onChange={(e) => upd("role", e.target.value)} className={selCls}>
                <option value="SR">SR — सत्संग रीडर</option>
                <option value="SK">SK — सत्संग कर्ता</option>
              </select>
            </div>
          )}
          <div>
            <Label className="text-xs text-[#5a2a24]">स्थान</Label>
            <HindiInput testid="person-place" value={f.place || ""} onChange={(v) => upd("place", v)} placeholder="e.g. Palia Kalan" className={inCls} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-[#5a2a24]">क्षेत्र समूह</Label>
              <select data-testid="person-group" value={f.group} onChange={(e) => upd("group", e.target.value)} className={selCls}>
                {Object.entries(GROUPS).map(([k, v]) => (
                  <option key={k} value={k}>{v.split(" (")[0]}</option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-xs text-[#5a2a24]">मोबाइल</Label>
              <Input data-testid="person-mobile" inputMode="tel" value={f.mobile || ""} onChange={(e) => upd("mobile", e.target.value)} className={inCls} />
            </div>
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={onClose} className="text-[#5a2a24]">रद्द करें</Button>
          <Button data-testid="person-save" disabled={saving} onClick={save} className="bg-gradient-to-b from-[#7a1f1f] to-[#5e1414] border border-[#431010] text-[#f3ead6] font-semibold">
            सहेजें
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
